import React, { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MarketSaleRowPro from "./MarketSaleRowPro";
import { getPropertyId } from "./utils";

/** Stable id for sortable row (listingID). */
export function getRowId(item, index) {
  const id = getPropertyId(item);
  return id != null ? `row-${String(id).replace(/\s/g, "_")}` : `row-${index}`;
}

function SortableTableRow({ item, index, marketSaleRowProps }) {
  const id = getRowId(item, index);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    ...(isDragging ? { opacity: 0.35, transition } : { transform: CSS.Transform.toString(transform), transition }),
  };
  const reorderHandle = (
    <span className="reorder-handle-in-cell-inner" {...attributes} {...listeners} aria-label="Drag to reorder">
      <span className="reorder-icon-box">
        <DragIndicatorIcon fontSize="small" />
      </span>
    </span>
  );
  return (
    <MarketSaleRowPro
      properties={[item]}
      asSingleRow
      wrapRow
      rowIndex={index}
      reorderHandle={reorderHandle}
      trProps={{ ref: setNodeRef, style, className: isDragging ? "is-dragging" : "" }}
      {...marketSaleRowProps}
    />
  );
}

function DragOverlayRow({ item, marketSaleRowProps, hidePropertyTypeColumn }) {
  const reorderHandle = (
    <span className="reorder-handle-in-cell-inner" aria-label="Drag to reorder">
      <span className="reorder-icon-box">
        <DragIndicatorIcon fontSize="small" />
      </span>
    </span>
  );
  return (
    <table
      className={`regular-sales-table mini-table-drag-overlay ${hidePropertyTypeColumn ? "hide-property-type" : ""}`}
    >
      <tbody>
        <MarketSaleRowPro properties={[item]} asSingleRow wrapRow reorderHandle={reorderHandle} {...marketSaleRowProps} />
      </tbody>
    </table>
  );
}

/**
 * Reorderable table body wrapper using @dnd-kit
 */
export default function ReorderableTable({
  miniTableProperties = [],
  marketSaleRowProps,
  hidePropertyTypeColumn,
  onReorder,
  onDragStart,
}) {
  const itemIds = useMemo(
    () => (miniTableProperties || []).map((item, i) => getRowId(item, i)),
    [miniTableProperties]
  );
  const [activeId, setActiveId] = useState(null);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    onDragStart?.(event);
  }, [onDragStart]);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || !onReorder || active.id === over.id) return;
      const items = miniTableProperties || [];
      const oldIndex = items.findIndex((item, i) => getRowId(item, i) === active.id);
      const newIndex = items.findIndex((item, i) => getRowId(item, i) === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(items, oldIndex, newIndex);
      const withSortingOrder = reordered.map((item, i) => ({ ...item, _sortingOrder: Number(i) }));
      onReorder(withSortingOrder);
    },
    [miniTableProperties, onReorder]
  );

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    const items = miniTableProperties || [];
    const idx = items.findIndex((item, i) => getRowId(item, i) === activeId);
    return idx >= 0 ? items[idx] : null;
  }, [activeId, miniTableProperties]);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <tbody>
          {(miniTableProperties || []).map((item, index) => (
            <SortableTableRow
              key={getRowId(item, index)}
              item={item}
              index={index}
              marketSaleRowProps={marketSaleRowProps}
            />
          ))}
        </tbody>
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <DragOverlayRow
            item={activeItem}
            marketSaleRowProps={marketSaleRowProps}
            hidePropertyTypeColumn={hidePropertyTypeColumn}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
